import { UploadOutlined } from '@ant-design/icons';
import { Button, Card, Form, Table, Tooltip, Upload, UploadFile } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import * as Component from 'components';
import { libraryService } from 'content/library/service';
import { libraryStore } from 'content/library/stores/libraryStore';
import { LibraryInterface } from 'content/library/types/contentType';
import { videoStatusService } from 'content/service/videoStatusService';
import { ContentStreamManifestInterface, VideoStatusInterface } from 'content/type';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UploadVideoInterface } from 'type/uploadVideoType';
import transcodeHandlers from '../../handlers/TranscodeHandlers';
import { Subtitle } from './Subtitle';
import './Transcode.css';
import TranscodeFileUpload from './TranscodeFileUpload';

export const TranscodeComponent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [ableToTranscode, setAbleToTranscode] = useState<boolean>(false);
  const [contentStreamManifests, setContentStreamManifests] = useState<
    ContentStreamManifestInterface[]
  >([]);
  const [originalVideoUrl, setOriginalVideoUrl] = useState<string>('');
  const [videoHistory, setVideoHistory] = useState<VideoStatusInterface[]>([]);
  const [uploadVideo, setUploadVideo] = useState<UploadVideoInterface>();

  const {
    handleRefresh,
    // handleStartTranscode,
    // handleRetranscodeWithEmbeddedSubtitle,
    handleUpdateVideoManifest,
    handleSaveButtonClick,
  } = transcodeHandlers({
    id: Number(id),
    setContent: setContentStreamManifests,
  });

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const library: LibraryInterface = await libraryService.fetchLibrary(Number(id));
      const contentStreamManifests = library.content_stream_manifests;
      setContentStreamManifests(
        contentStreamManifests.filter(
          (manifest: ContentStreamManifestInterface) => manifest.type !== 'hls-odk'
        )
      );
      const contentFile = library.contents_file;
      if (contentFile) {
        setOriginalVideoUrl(contentFile.file || contentFile.external_url || '');
      }
      setAbleToTranscode(contentFile !== null);

      if (library.video_history && library.video_history.length > 0) {
        setVideoHistory(library.video_history);
      } else {
        const videoStatus = await videoStatusService.fetch(
          new URLSearchParams({ content__id: id || '' })
        );
        library.video_history = videoStatus.results;
        libraryStore.setLibrary(Number(id), library);
        setVideoHistory(videoStatus.results || []);
      }
    };
    fetchData();
  }, [id]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      render: (text: string): React.ReactNode => {
        return <span>{new Date(text).toLocaleString()}</span>;
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Details',
      dataIndex: 'extra_info',
      key: 'extra_info',
      render: (text: string): React.ReactNode => {
        return (
          <Tooltip title={JSON.stringify(text)}>
            <span>details</span>
          </Tooltip>
        );
      },
    },
  ];

  let isVideoStatusProcessing = false;
  if (videoHistory.length > 0) {
    const latestVideoStatus = videoHistory[0];
    if (latestVideoStatus.status !== 'COMPLETED') {
      isVideoStatusProcessing = true;
    }
  }

  return (
    <div>
      <Card title="Video Status" className="transcodeCard">
        {isVideoStatusProcessing ? (
          <div className="videoStatusProcessing">Processing</div>
        ) : (
          <div className="videoStatusNormal">Normal</div>
        )}
      </Card>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <p style={{ margin: 0, height: '32px', lineHeight: '32px' }}>Original Video</p>
            <Upload
              accept="video/*"
              beforeUpload={() => false}
              showUploadList={false}
              fileList={uploadVideo ? [uploadVideo] : []}
              onChange={(info: UploadChangeParam<UploadFile<UploadVideoInterface>>) => {
                setUploadVideo({ ...info.fileList[0], state: 'normal', speed: 0, percent: 0 });
              }}
            >
              <Button style={{ marginLeft: '10px', height: '32px' }} icon={<UploadOutlined />} />
            </Upload>
          </div>
        }
        className="transcodeCard"
      >
        <Tooltip title={originalVideoUrl}>
          <div className="originalVideoContainer">
            <a href={originalVideoUrl} target="_blank" rel="noopener noreferrer">
              {originalVideoUrl.split('/').pop()}
            </a>
          </div>
        </Tooltip>
      </Card>
      <TranscodeFileUpload uploadVideo={uploadVideo} />
      <Card title="Transcode" className="transcodeCard">
        {/* <button
          className="startTranscodeButton"
          onClick={() => handleStartTranscode()}
          style={{ marginRight: '10px' }}
        >
          Start Transcode
        </button> */}
        <button
          className="refreshButton"
          onClick={() => handleRefresh()}
          disabled={!ableToTranscode}
          style={{ marginRight: '10px' }}
        >
          Refresh
        </button>
        {/* <button
          className="retranscodeWithEmbeddedSubtitleButton"
          onClick={() => handleRetranscodeWithEmbeddedSubtitle()}
        >
          Transcode embedded subtitle
        </button> */}
        <button
          className="updateVideoManifestButton"
          onClick={() => handleUpdateVideoManifest()}
          style={{ marginLeft: '10px' }}
        >
          Refresh Video URL
        </button>
        {ableToTranscode ? (
          <></>
        ) : (
          <div className="transcodeInfo">
            <span>This video is not able to transcode</span>
          </div>
        )}
        <div className="transcodeContainer">
          {contentStreamManifests.length === 0 ? (
            <div>Empty Transcode Video</div>
          ) : (
            contentStreamManifests.map((manifest, index) => (
              <div key={index} className="transcodeItem">
                <div className="transcodeHeader">
                  <span className="transcodePreset">
                    {manifest.type} {manifest.preset}
                  </span>
                  <span className={`transcodeStatus ${manifest.status}`}>{manifest.status}</span>
                </div>
                {manifest.stream_url && (
                  <div className="transcodeUrl">
                    <span className="urlLabel">URL:</span>
                    <a href={manifest.stream_url}>
                      {manifest.stream_url.length > 30
                        ? manifest.stream_url.substring(0, 30) + '...'
                        : manifest.stream_url}
                    </a>
                    <div className="transcodePreview">
                      <span className="previewLabel">Preview:</span>
                      <Component.VideoPlayerComponent
                        options={{
                          sources: [{ src: manifest.stream_url, type: 'application/x-mpegURL' }],
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </Card>
      <Card title="Subtitle" className="transcodeCard">
        <Subtitle form={form} />
      </Card>
      <Card title="Video History" className="transcodeCard">
        <Table dataSource={videoHistory} columns={columns} />
      </Card>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Button
          type="primary"
          onClick={() => handleSaveButtonClick(Number(id), form, uploadVideo, setUploadVideo)}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default TranscodeComponent;
